import { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Container, Typography, Button } from '@mui/material'
import { Home, Refresh, BugReport } from '@mui/icons-material'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo
    })

    // TODO: Log to error tracking service (e.g., Sentry, Application Insights)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
    window.location.href = '/'
  }

  handleRefresh = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #e53e3e 0%, #fc8181 100%)',
            color: 'white'
          }}
        >
          <Container maxWidth="md">
            <Box textAlign="center">
              <BugReport
                sx={{
                  fontSize: { xs: '4rem', md: '6rem' },
                  mb: 2,
                  opacity: 0.9
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                Something Went Wrong
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                We encountered an unexpected error. This has been logged and our team will investigate.
                Please try refreshing the page or returning to the home page.
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    borderRadius: 2,
                    textAlign: 'left',
                    maxWidth: '800px',
                    mx: 'auto',
                    overflow: 'auto'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600, fontSize: '0.9rem' }}
                  >
                    Error Details (Development Mode Only):
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace'
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Home />}
                  onClick={this.handleReset}
                  sx={{
                    bgcolor: 'white',
                    color: '#e53e3e',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    },
                    py: 1.5,
                    px: 4
                  }}
                >
                  Go Home
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Refresh />}
                  onClick={this.handleRefresh}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    },
                    py: 1.5,
                    px: 4
                  }}
                >
                  Refresh Page
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      )
    }

    return this.props.children
  }
}
